const STORAGE_KEY = 'pre_signup_flow';
const COMPLETED_KEY = 'pre_signup_completed';

export interface PreSignupState {
  step: number;
  email: string;
  role: string;
  goals: string[];
  experience: string[];
  ghlContactId: string;
}

export interface PreSignupCompletedSnapshot {
  email: string;
  role: string;
  goals: string[];
  experience: string[];
  savedAt: number;
}

const defaultState: PreSignupState = {
  step: 1,
  email: '',
  role: '',
  goals: [],
  experience: [],
  ghlContactId: '',
};

function maxStepAllowed(s: PreSignupState): number {
  if (!s.email.trim()) return 1;
  if (!s.role) return 2;
  if (!s.goals.length) return 3;
  if (!s.experience.length) return 4;
  return 5;
}

function sanitize(state: unknown): PreSignupState {
  const o = state as Record<string, unknown>;
  const safe: PreSignupState = {
    step: Number.isFinite(o?.step) ? Math.floor(Number(o.step)) : 1,
    email: typeof o?.email === 'string' ? o.email : '',
    role: typeof o?.role === 'string' ? o.role : '',
    goals: Array.isArray(o?.goals) ? o.goals.filter((x) => typeof x === 'string') : [],
    experience: Array.isArray(o?.experience)
      ? o.experience.filter((x) => typeof x === 'string')
      : [],
    ghlContactId: typeof o?.ghlContactId === 'string' ? o.ghlContactId : '',
  };

  const max = maxStepAllowed(safe);

  return {
    ...safe,
    step: Math.min(Math.max(1, safe.step), max, 5),
  };
}

export function loadPreSignupFlowState(): PreSignupState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;

    const parsed = JSON.parse(raw);
    return sanitize(parsed);
  } catch {
    return defaultState;
  }
}

export function savePreSignupFlowState(state: PreSignupState) {
  try {
    const safe = sanitize(state);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
  } catch {
    // ignore
  }
}

export function clearPreSignupFlowState() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

function isValidCompleted(p: unknown): p is PreSignupCompletedSnapshot {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.email === 'string' &&
    o.email.trim().length > 0 &&
    typeof o.role === 'string' &&
    o.role.length > 0 &&
    Array.isArray(o.goals) &&
    o.goals.length > 0 &&
    o.goals.every((x) => typeof x === 'string') &&
    Array.isArray(o.experience) &&
    o.experience.length > 0 &&
    o.experience.every((x) => typeof x === 'string') &&
    typeof o.savedAt === 'number'
  );
}

export function savePreSignupCompleted(
  data: Pick<PreSignupCompletedSnapshot, 'email' | 'role' | 'goals' | 'experience'>
) {
  try {
    const payload: PreSignupCompletedSnapshot = {
      ...data,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(COMPLETED_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

export function loadPreSignupCompleted(): PreSignupCompletedSnapshot | null {
  try {
    const raw = sessionStorage.getItem(COMPLETED_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return isValidCompleted(p) ? p : null;
  } catch {
    return null;
  }
}

export function clearPreSignupCompleted() {
  try {
    sessionStorage.removeItem(COMPLETED_KEY);
  } catch {
    // ignore
  }
}

export function seedPreSignupEmailFromSso(email: string, ghlContactId: string) {
  try {
    const current = loadPreSignupFlowState();
    const next: PreSignupState = {
      ...current,
      email,
      step: 2,
      role: '',
      goals: [],
      experience: [],
      ghlContactId: ghlContactId,
    };
    savePreSignupFlowState(next);
  } catch {
    // ignore
  }
}
